/**
 * Who each Perfex staff member is on this platform.
 *
 * WHY THIS CARD SITS ABOVE THE RUN BUTTON. The import already knows how to fill
 * `Project.members`, `Task.assignees` and `Ticket.assignedTo` - the mappers have
 * read those source tables since the module was written. What it cannot do is
 * decide WHO a staff row is when the two systems share no address and no name,
 * and measured against the real source that is every row. Running the import
 * before answering this writes empty owners across 23 projects and 112 tasks,
 * successfully and silently. So the question comes before the button.
 *
 * THE ORDER OF THE ROWS IS THE POINT. The server ranks them by how much work
 * rides on the answer, because five names look like five equal questions until
 * the counts are beside them and two of them turn out to carry everything. A
 * row owning nothing is not worth a decision and should read that way.
 *
 * SAVING DOES NOT IMPORT. This writes the map and nothing else; the next run is
 * what applies it. Making a correction rewrite ownership across seventeen
 * tables on its own would be an event nobody pressed.
 */
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import {
  putImportStaff,
  type StaffReview,
  type StaffReviewRow,
  type StaffVia,
} from "../../../../lib/crmImport";
import { Btn, Card, SelectField, StatusPill, useToast } from "../../../ui";

/** Sentinel for "leave this row unowned", kept apart from "nobody decided". */
const NOBODY = "__nobody__";

interface Props {
  review: StaffReview | null;
  onSaved: (next: StaffReview) => void;
}

export function StaffCard({ review, onSaved }: Props) {
  const t = useT();
  const toast = useToast();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  if (!review || review.staff.length === 0) return null;

  /** What the select shows: the operator's unsaved pick, else what holds now. */
  const valueFor = (row: StaffReviewRow): string => {
    const pending = draft[row.staffId];
    if (pending !== undefined) return pending;
    if (row.via === "excluded") return NOBODY;
    // A match is shown as the user it found, so the operator can confirm it by
    // saving rather than having to retype an answer that is already right.
    return row.userId ?? "";
  };

  const dirty = Object.keys(draft).length > 0;

  async function save() {
    if (!review) return;
    setSaving(true);
    try {
      // The whole map is rebuilt from what the screen shows, not from the diff:
      // the route needs absence to mean "nobody decided", so a row left on the
      // empty option has to arrive as a missing key rather than as an empty one.
      const links: Record<string, string | null> = {};
      for (const row of review.staff) {
        const value = valueFor(row);
        if (value === NOBODY) links[row.staffId] = null;
        else if (value) links[row.staffId] = value;
      }
      const { review: next } = await putImportStaff(links);
      setDraft({});
      if (next) onSaved(next);
      toast(t("crmImport.staff.saved"));
    } catch {
      toast(t("crmImport.staff.saveError"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="crm-prow-n">{t("crmImport.staff.title")}</h3>
          <p className="crm-prow-s mt-1">{t("crmImport.staff.body")}</p>
        </div>
        <Btn tone="dark" onClick={() => void save()} disabled={!dirty || saving}>
          {saving ? <Loader2 className="size-4 animate-spin" /> : null}
          {t("crmImport.staff.save")}
        </Btn>
      </div>

      {review.unresolvedOwningWork > 0 ? (
        <div className="crm-note mt-3">
          <div className="crm-note-t">{t("crmImport.staff.warnTitle")}</div>
          <p className="crm-note-p">
            {t("crmImport.staff.warnBody", { n: review.unresolvedOwningWork })}
          </p>
        </div>
      ) : null}

      <div className="crm-rows mt-3">
        {review.staff.map((row) => {
          const owns = row.rowsOwned > 0;
          return (
            <div
              key={row.staffId}
              className="crm-list-row-soft grid items-center gap-3 sm:grid-cols-[1.3fr_auto_1.1fr]"
            >
              <div className="min-w-0">
                <div className="crm-prow-n crm-trunc">
                  {row.name ?? t("crmImport.staff.noName")}
                  {!row.active ? (
                    <span className="crm-prow-s ml-2">{t("crmImport.staff.inactive")}</span>
                  ) : null}
                </div>
                <div className="crm-prow-s crm-trunc">{row.email ?? t("crmImport.staff.noEmail")}</div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <StatusPill tone={owns ? "wt" : "mut"}>
                  {owns
                    ? t("crmImport.staff.owns", {
                        projects: row.workload.projects,
                        tasks: row.workload.tasks,
                        hours: row.workload.timeEntries,
                      })
                    : t("crmImport.staff.ownsNothing")}
                </StatusPill>
                <ViaMark via={row.via} />
              </div>

              <SelectField
                value={valueFor(row)}
                onChange={(next) => setDraft((prev) => ({ ...prev, [row.staffId]: next }))}
              >
                <option value="">{t("crmImport.staff.undecided")}</option>
                {review.users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.email})
                  </option>
                ))}
                <option value={NOBODY}>{t("crmImport.staff.nobody")}</option>
              </SelectField>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/**
 * Where the current answer came from.
 *
 * A decision and a coincidence are drawn differently on purpose: an operator
 * looking at a filled-in row needs to know whether somebody chose it or whether
 * two addresses happened to line up, because only one of those is theirs. The
 * unresolved case draws nothing - the empty select beside it already says so,
 * and a second mark for the same fact reads as a second problem.
 */
function ViaMark({ via }: { via: StaffVia }) {
  const t = useT();
  if (via === "unresolved") return null;
  const label =
    via === "link"
      ? t("crmImport.staff.viaLink")
      : via === "excluded"
        ? t("crmImport.staff.viaExcluded")
        : via === "email"
          ? t("crmImport.staff.viaEmail")
          : t("crmImport.staff.viaName");
  return <span className="crm-prow-s">{label}</span>;
}
